import { Alert, Button, Checkbox, Group, Loader, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconCheck, IconRefresh, IconX } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_CHANNEL_PHONE,
  SUBMIT_ATTESTATION_LABEL,
  SUBMISSION_ATTESTATION_VERSION,
  attestationCopy,
} from "../constants/matching";
import { jobsApi, submissionsApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { formatCities } from "../lib/format";
import { selectedForSubmitSet, type BulkItem } from "../pages/bulkTypes";
import { axioTokens } from "../theme";

type RowState = "idle" | "creating" | "ok" | "error";

const CONTACT_CHANNEL_SELECT_DATA = [...CONTACT_CHANNEL_OPTIONS];
const CONTACT_CHANNEL_LABEL = "How you contacted them";
const CONTACTED_ON_LABEL = "Contacted on";
const BULK_ATTESTATION_SUBJECT = "these candidates";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface BulkSubmitModalProps {
  opened: boolean;
  onClose: () => void;
  items: BulkItem[];
  onPatch: (id: string, patch: Partial<BulkItem>) => void;
  onBatchComplete: () => void;
}

function rowIcon(state: RowState) {
  if (state === "error") return <IconX size={axioTokens.unit[16]} color={axioTokens.danger} />;
  if (state === "ok") return <IconCheck size={axioTokens.unit[16]} color={axioTokens.money} />;
  if (state === "creating") return <Loader size="xs" color="violet" />;
  return (
    <Text size="xs" c="dimmed">
      ○
    </Text>
  );
}

export function BulkSubmitModal({
  opened,
  onClose,
  items,
  onPatch,
  onBatchComplete,
}: BulkSubmitModalProps) {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [channel, setChannel] = useState<string | null>(CONTACT_CHANNEL_PHONE);
  const [contactedOn, setContactedOn] = useState(todayIso());
  const [confirmed, setConfirmed] = useState(false);

  const toSubmit = useMemo(() => selectedForSubmitSet(items), [items]);
  const submittedCount = toSubmit.filter((item) => rowState[item.id] === "ok").length;
  const failedCount = toSubmit.filter((item) => rowState[item.id] === "error").length;
  const allAttempted =
    toSubmit.length > 0 && toSubmit.every((item) => rowState[item.id] === "ok" || rowState[item.id] === "error");

  const jobsQuery = useQuery({
    queryKey: ["bulk-open-jobs"],
    queryFn: () => jobsApi.list({ status: "open", page_size: 100 }),
    enabled: opened,
  });

  const jobOptions = useMemo(
    () =>
      (jobsQuery.data?.items ?? []).map((job) => ({
        value: job.id,
        label: `${job.title} — ${job.company_name} (${formatCities(job.cities) || "—"})`,
      })),
    [jobsQuery.data?.items],
  );
  const selectedJob = (jobsQuery.data?.items ?? []).find((job) => job.id === jobId);
  const attestationLabel = attestationCopy({
    candidate: BULK_ATTESTATION_SUBJECT,
    date: contactedOn,
    channel: channel ?? CONTACT_CHANNEL_PHONE,
    jobTitle: selectedJob?.title ?? "this job",
  });
  const futureDate = contactedOn > todayIso();
  const canSubmitBatch =
    Boolean(jobId) &&
    toSubmit.length > 0 &&
    Boolean(channel) &&
    Boolean(contactedOn) &&
    confirmed &&
    !futureDate;

  function setState(id: string, state: RowState, error?: string) {
    setRowState((previous) => ({ ...previous, [id]: state }));
    setRowError((previous) => {
      if (!error) {
        const next = { ...previous };
        delete next[id];
        return next;
      }
      return { ...previous, [id]: error };
    });
  }

  async function submitOne(item: BulkItem, selectedJobId: string) {
    if (!item.candidateId) return;
    setState(item.id, "creating");
    try {
      const submission = await submissionsApi.create({
        job_id: selectedJobId,
        candidate_id: item.candidateId,
        pitch_note: item.callNotes || undefined,
        attestation: {
          confirmed: true,
          contact_channel: channel ?? CONTACT_CHANNEL_PHONE,
          contacted_on: contactedOn,
          version: SUBMISSION_ATTESTATION_VERSION,
        },
      });
      onPatch(item.id, { submissionId: submission.id, submitError: null });
      setState(item.id, "ok");
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "Could not submit this candidate";
      onPatch(item.id, { submitError: message });
      setState(item.id, "error", message);
    }
  }

  async function submitAll() {
    if (!jobId) return;
    setRunning(true);
    for (const item of toSubmit) {
      if (rowState[item.id] === "ok") continue;
      await submitOne(item, jobId);
    }
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    setRunning(false);
    onBatchComplete();
  }

  async function retryOne(item: BulkItem) {
    if (!jobId) return;
    setRunning(true);
    await submitOne(item, jobId);
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    setRunning(false);
    onBatchComplete();
  }

  function handleClose() {
    if (!running) onClose();
  }

  function submitAnotherJob() {
    setJobId(null);
    setRowState({});
    setRowError({});
    setConfirmed(false);
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Submit saved candidates" size="lg">
      <Stack gap={axioTokens.unit[32]}>
        <Alert color="violet" variant="light">
          These candidates are already in your pool. Choose a job to submit them to.
        </Alert>
        <Select
          label="Select an open job"
          placeholder="Search by job title…"
          searchable
          data={jobOptions}
          value={jobId}
          allowDeselect={false}
          onChange={(value) => {
            if (value) setJobId(value);
          }}
          disabled={running || allAttempted}
        />
        <Select
          label={CONTACT_CHANNEL_LABEL}
          data={CONTACT_CHANNEL_SELECT_DATA}
          value={channel}
          allowDeselect={false}
          onChange={(value) => {
            if (value) setChannel(value);
          }}
          disabled={running || allAttempted}
        />
        <TextInput
          type="date"
          label={CONTACTED_ON_LABEL}
          value={contactedOn}
          max={todayIso()}
          onChange={(event) => setContactedOn(event.currentTarget.value)}
          disabled={running || allAttempted}
        />
        <Checkbox
          label={attestationLabel}
          checked={confirmed}
          onChange={(event) => setConfirmed(event.currentTarget.checked)}
          disabled={running || allAttempted || !jobId}
        />
        <Text size="xs" c="dimmed">
          {SUBMIT_ATTESTATION_LABEL}
        </Text>

        <Stack gap="xs">
          {toSubmit.map((item) => {
            const state = rowState[item.id] ?? "idle";
            return (
              <div key={item.id} className="bulkSubmitRow">
                {rowIcon(state)}
                <div className="bulkSubmitRowName">
                  {item.parsedName || item.fileName}
                  {item.callNotes ? <div className="bulkSubmitRowNotes">{item.callNotes}</div> : null}
                  {rowError[item.id] ? (
                    <Text size="xs" c="red">
                      {rowError[item.id]}
                    </Text>
                  ) : null}
                </div>
                {state === "error" && jobId ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    leftSection={<IconRefresh size={axioTokens.unit[14]} />}
                    onClick={() => void retryOne(item)}
                    disabled={running}
                  >
                    Retry
                  </Button>
                ) : null}
              </div>
            );
          })}
        </Stack>

        {allAttempted ? (
          <Alert color={failedCount > 0 ? "yellow" : "green"} variant="light">
            {submittedCount} of {toSubmit.length} submitted
            {failedCount > 0 ? ` · ${failedCount} failed` : " successfully"}
          </Alert>
        ) : null}

        <Group justify="flex-end" gap="sm">
          {allAttempted ? (
            <>
              <Button variant="default" onClick={submitAnotherJob} disabled={running}>
                Submit same set to another job
              </Button>
              <Button color="violet" onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <Button
              color="violet"
              onClick={() => void submitAll()}
              loading={running}
              disabled={!canSubmitBatch}
            >
              Submit {toSubmit.length} candidate{toSubmit.length === 1 ? "" : "s"}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
